// WHY (#2175): post-send editor for CONTAINER request-level weight/volume.
//   Mirrors DraftContainerTotalsCard (#2116) in layout but is wired to the
//   `PATCH /requests/:id/container-totals` mutation instead of the draft
//   store. Rendered from ItemsTab for CONTAINER requests in the backfill
//   allow-list statuses.
//
// @aiPurpose lets a manager correct the `weight`/`volume` stored on a
//   CONTAINER request that was already sent. The mutation writes both
//   columns atomically with an audit row; `resolveAggregateTotal`
//   (#2116) picks up the stored value automatically on the next read.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eyebrow, Input } from '../../../ui/index.js';
import { Card, ErrorChip, Field, Grid2 } from './DraftEditorPrimitives.js';
import { useDecimalTextInput } from './useDecimalTextInput.js';
import { useSetContainerTotals } from '../mutations.js';
import type { RequestView } from '../types.js';

export function ContainerTotalsEditorCard({ request }: { request: RequestView }) {
  const { t } = useTranslation(['requests']);
  const [weight, setWeight] = useState<number | null>(request.weight);
  const [volume, setVolume] = useState<number | null>(request.volume);
  const weightField = useDecimalTextInput(weight, (v) => setWeight(v));
  const volumeField = useDecimalTextInput(volume, (v) => setVolume(v));
  const mutation = useSetContainerTotals(request.id);

  const canSave = weight !== null && weight > 0 && volume !== null && volume > 0;

  return (
    <Card>
      <div data-testid="container-totals-editor-card">
        <Eyebrow>{t('requests:draft.containerTotals.section')}</Eyebrow>
        <div style={{ height: 12 }} />
        <Grid2>
          <Field label={t('requests:draft.containerTotals.weight')} required>
            <Input
              monospace
              value={weightField.text}
              onChange={weightField.onChange}
              onBlur={weightField.onBlur}
              placeholder="0"
              ariaLabel={t('requests:draft.containerTotals.weight')}
            />
          </Field>
          <Field label={t('requests:draft.containerTotals.volume')} required>
            <Input
              monospace
              value={volumeField.text}
              onChange={volumeField.onChange}
              onBlur={volumeField.onBlur}
              placeholder="0"
              ariaLabel={t('requests:draft.containerTotals.volume')}
            />
          </Field>
        </Grid2>
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            type="button"
            disabled={!canSave || mutation.isPending}
            onClick={() => {
              if (weight !== null && weight > 0 && volume !== null && volume > 0) {
                mutation.mutate({ weight, volume });
              }
            }}
            style={{
              padding: '6px 14px',
              fontSize: 13,
              borderRadius: 6,
              border: '1px solid var(--border-1)',
              background: 'var(--bg-2, var(--bg-1))',
              color: 'var(--fg-1)',
              cursor: 'pointer',
              opacity: !canSave || mutation.isPending ? 0.5 : 1,
            }}
          >
            {mutation.isPending ? '…' : t('requests:containerTotalsBackfill.save')}
          </button>
          {mutation.isSuccess && (
            <span style={{ fontSize: 12, color: 'var(--status-success-fg)' }}>
              {t('requests:containerTotalsBackfill.saved')}
            </span>
          )}
          {mutation.isError && <ErrorChip text={t('requests:containerTotalsBackfill.error')} />}
        </div>
      </div>
    </Card>
  );
}
